/**
 * ArticleSelector.tsx
 * 
 * Componente per la selezione ibrida degli articoli nell'applicazione Ufficio Stile.
 * Supporta sia la ricerca in dati esterni che la creazione di nuovi articoli inline.
 * Implementa il pattern "Approccio Ibrido" per gestire articoli esistenti e nuovi.
 */

import React, { useState } from 'react';
import {
  Stack,
  Group,
  Button,
  TextInput,
  Select,
  Card,
  Text,
  Badge,
  ActionIcon,
  Divider,
  Alert
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch, IconPlus, IconX, IconPackage } from '@tabler/icons-react';
import { useSearchExternalArticles } from '../../../hooks';
import { ExternalArticle } from '../../../types/api';
import { ArticleItem } from './ArticlesSection';

interface ArticleSelectorProps {
  onSelect: (article: Omit<ArticleItem, 'id'>) => void;
  onCancel: () => void;
  clientId?: string;
}

export function ArticleSelector({ onSelect, onCancel, clientId }: ArticleSelectorProps) {
  const [mode, setMode] = useState<'search' | 'create'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
  
  // Form per nuovo articolo
  const [newArticle, setNewArticle] = useState({
    articleCode: '',
    articleName: '',
    category: ''
  });

  // Hook per ricerca articoli esterni
  const { data: externalArticles, isLoading, error: searchError } = useSearchExternalArticles(debouncedSearchQuery);

  // Seleziona articolo esistente
  const handleSelectExisting = (article: ExternalArticle) => {
    onSelect({
      articleId: article.id,
      articleCode: article.code,
      articleName: article.name,
      category: article.category,
      unitPrice: article.price,
      quantity: 1
    });
  };

  // Crea nuovo articolo
  const handleCreateNew = () => {
    if (newArticle.articleCode && newArticle.articleName) {
      onSelect({
        articleCode: newArticle.articleCode,
        articleName: newArticle.articleName,
        category: newArticle.category || undefined,
        quantity: 1
      });
    }
  };

  // Reset form nuovo articolo
  const resetNewArticleForm = () => {
    setNewArticle({
      articleCode: '',
      articleName: '',
      category: ''
    });
  };

  const isCreateFormValid = newArticle.articleCode && newArticle.articleName;

  return (
    <Card withBorder>
      <Stack gap="md">
        {/* Header con toggle modalità */}
        <Group justify="space-between">
          <Text fw={500}>Aggiungi Articolo</Text>
          <Group>
            <Button
              variant={mode === 'search' ? 'filled' : 'outline'}
              size="xs"
              onClick={() => setMode('search')}
            >
              Cerca Esistente
            </Button>
            <Button
              variant={mode === 'create' ? 'filled' : 'outline'}
              size="xs"
              onClick={() => setMode('create')}
            >
              Crea Nuovo
            </Button>
            <ActionIcon color="gray" onClick={onCancel}>
              <IconX size={16} />
            </ActionIcon>
          </Group>
        </Group>

        <Divider />

        {/* Modalità ricerca */}
        {mode === 'search' && (
          <Stack gap="md">
            <TextInput
              placeholder="Cerca articoli per codice o nome..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => {
                const value = e.currentTarget?.value || '';
                setSearchQuery(value);
              }}
            />

            {/* Risultati ricerca */}
            {debouncedSearchQuery.length > 2 && (
              <Stack gap="xs" mah={300} style={{ overflow: 'auto' }}>
                {isLoading ? (
                  <Text size="sm" c="dimmed" ta="center" py="xl">
                    Ricerca in corso...
                  </Text>
                ) : searchError ? (
                  <Alert color="red" title="Errore">
                    Errore durante la ricerca. Riprova o crea un nuovo articolo.
                  </Alert>
                ) : externalArticles && Array.isArray(externalArticles) && externalArticles.length > 0 ? (
                  externalArticles.map((article) => (
                    <Card
                      key={article.id + '-' + article.code}
                      withBorder
                      padding="md"
                      style={{ 
                        cursor: 'pointer', 
                        transition: 'all 0.2s ease',
                        minHeight: '50px',
                        ':hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }
                      }}
                      onClick={() => handleSelectExisting(article)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        e.currentTarget.style.backgroundColor = 'var(--mantine-color-blue-0)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '';
                        e.currentTarget.style.backgroundColor = '';
                      }}
                    >
                      <Stack gap="xs">
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                            <IconPackage size={20} color="var(--mantine-color-blue-6)" />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text fw={600} size="sm" lineClamp={1}>
                                {article.code} - {article.name}
                              </Text>
                            </div>
                          </Group>
                          {article.price && (
                            <Badge size="lg" color="green" variant="light">
                              €{article.price.toFixed(2)}
                            </Badge>
                          )}
                        </Group>
                        
                        <Text size="sm" c="dimmed" lineClamp={2}>
                          {article.code} - {article.description || article.name}
                        </Text>
                        
                        {article.category && (
                          <Badge size="sm" variant="dot" color="blue">
                            {article.category}
                          </Badge>
                        )}
                      </Stack>
                    </Card>
                  ))
                ) : (
                  <Alert color="gray" title="Nessun risultato">
                    <Stack gap="sm">
                      <Text size="sm">Nessun articolo trovato con questi criteri</Text>
                      <Button
                        size="sm"
                        variant="light"
                        leftSection={<IconPlus size={16} />}
                        onClick={() => setMode('create')}
                      >
                        Crea Nuovo Articolo
                      </Button>
                    </Stack>
                  </Alert>
                )}
              </Stack>
            )}

            {debouncedSearchQuery.length <= 2 && (
              <Text size="sm" c="dimmed" ta="center" py="md">
                💡 Digita almeno 3 caratteri per iniziare la ricerca
              </Text>
            )}
          </Stack>
        )}

        {/* Modalità creazione */}
        {mode === 'create' && (
          <Stack gap="md">
            <TextInput
              label="Codice Articolo"
              placeholder="Inserisci codice articolo"
              required
              value={newArticle.articleCode}
              onChange={(e) => {
                const value = e.currentTarget?.value || '';
                setNewArticle(prev => ({ 
                  ...prev, 
                  articleCode: value 
                }));
              }}
            />
            
            <TextInput
              label="Nome Articolo"
              placeholder="Inserisci nome articolo"
              required
              value={newArticle.articleName}
              onChange={(e) => {
                const value = e.currentTarget?.value || '';
                setNewArticle(prev => ({ 
                  ...prev, 
                  articleName: value 
                }));
              }}
            />
            
            <TextInput
              label="Categoria"
              placeholder="Categoria (opzionale)"
              value={newArticle.category}
              onChange={(e) => {
                const value = e.currentTarget?.value || '';
                setNewArticle(prev => ({ 
                  ...prev, 
                  category: value 
                }));
              }}
            />

            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={() => {
                  resetNewArticleForm();
                  setMode('search');
                }}
              >
                Annulla
              </Button>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={handleCreateNew}
                disabled={!isCreateFormValid}
              >
                Aggiungi Articolo
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
